import { VisionApplication } from '../../application';
import { PublishTopic } from '../../events';
import { IPubSubSubscriber } from '../../pubsub';
import { WorkspaceConfiguration, WorkspaceDTO } from '../../service/workspace.service';
import { VisionWindowManager } from '../../window/manager';
import { VisionWindow } from '../../window/window';

export class WorkspaceWindow extends VisionWindow implements IPubSubSubscriber {
    private layout!: dhtmlXLayoutObject;
    private workspaceGrid!: dhtmlXGridObject;
    private toolbar!: dhtmlXToolbarObject;
    private form!: dhtmlXForm;
    public identifier = '1ce95967-2c45-46ce-9935-23210b7a3bd2';

    constructor(manager: VisionWindowManager) {
        const height = window.innerHeight * 0.75;
        const width = window.innerWidth * 0.45;
        super(manager, {
            id: 'workspace_window',
            title: 'Workspaces',
            x: 30,
            y: 30,
            width: width,
            height: height,
        });
        this.window.setText('Workspaces');

        this.buildUI();
        this.buildEvents();
        this.loadData();
    }

    private buildUI(): void {
        this.layout = this.window.attachLayout('2U');
        this.layout.cells('a').setText('Workspace');
        this.layout.cells('b').setText('Details');
        this.workspaceGrid = this.layout.cells('a').attachGrid();
        this.workspaceGrid.setHeader('Name', ',', ['text-align:left']);

        this.workspaceGrid.setColTypes('ro');
        this.workspaceGrid.init();

        this.toolbar = this.layout.cells('b').attachToolbar();
        this.toolbar.addButton('new', 0, 'New', 'icons/sign.png', '');
        this.toolbar.addButton('save', 1, 'Save', 'css/imgs/save.gif', '');
        this.toolbar.addSeparator('sep1', 2);
        this.toolbar.addButton('delete', 3, 'Delete', 'icons/clear.png', '');

        const form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 200,
                inputWidth: 130,
            },
            {
                name: 'id',
                type: 'input',
                label: 'ID',
                value: '',
                inputWidth: '400',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'name',
                type: 'input',
                label: 'Name',
                value: '',
                inputWidth: '400',
                offsetLeft: '25',
                validate: 'ValidAplhaNumeric',
                required: true,
            },
        ];

        this.form = this.layout.cells('b').attachForm(form_data);
        this.layout.cells('a').hideHeader();
        this.layout.cells('b').hideHeader();
    }

    private buildEvents() {
        VisionApplication.getInstance().pubsub.subscribe(PublishTopic.WORKSPACES_RELOADED, this);

        this.workspaceGrid.attachEvent('onRowSelect', (id) => {
            this.handlerRowSelect(id);
        });

        this.toolbar.attachEvent('onClick', (id) => {
            this.handleToolbar(id);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public notify(msg: string, _data?: any): void {
        switch (msg) {
            case PublishTopic.WORKSPACES_RELOADED: {
                this.workspaceGrid.clearAll(false);
                this.loadData();
                this.workspaceGrid.clearSelection();
                break;
            }
        }
    }

    private handleToolbar(id: string): void {
        switch (id) {
            case 'new': {
                this.form.setItemValue('id', '');
                this.form.setItemValue('name', '');
                this.workspaceGrid.clearSelection();
                break;
            }

            case 'save': {
                this.saveWorkspace();
                break;
            }

            case 'delete': {
                this.deleteWorkspace();
                break;
            }
        }
    }

    private handlerRowSelect(id: string | undefined): void {
        const app = VisionApplication.getInstance();
        const workspace = app.workspaceService.workspaces.get(id);
        if (workspace) {
            this.form.setItemValue('id', workspace.id);
            this.form.setItemValue('name', workspace.name);
        }
    }

    private loadData() {
        const app = VisionApplication.getInstance();
        app.workspaceService.workspaces.forEach((value, key) => {
            if (key) {
                this.workspaceGrid.addRow(key, [value.name], '', '', '', false);
            }
        });
    }

    //-----------------
    // Form functions
    //-----------------
    private saveWorkspace(): void {
        const app = VisionApplication.getInstance();
        const columns = app.layout.avl2Tab.getColumns();
        const name = this.form.getItemValue('name');
        const id = this.form.getItemValue('id');

        const config = {} as WorkspaceConfiguration;
        config.columns = columns;
        config.order = app.layout.avl2Tab.assetTree.tree.getColumnOrder();
        config.widths = app.layout.avl2Tab.assetTree.getColumnWidths();
        config.cellWidth = app.layout.avl2Tab.layout.cells('a').getWidth();
        config.mapType = app.layout.avl2Tab.mapPanel.map.map.getMapTypeId();

        const save = {} as WorkspaceDTO;
        save.config = config;
        save.name = name;
        save.id = id;

        if (save.id && save.id.length > 5) {
            app.workspaceService.updateWorkspace(save).then((data) => {
                if (data.parsedBody) {
                    dhtmlx.message({
                        title: 'Update Complete',
                        text: 'Workspace was updated',
                        type: 'alert-success',
                    });
                    app.workspaceService.refresh();
                }
            });
        } else {
            app.workspaceService.saveWorkspace(save).then((data) => {
                if (data.parsedBody) {
                    dhtmlx.message({
                        title: 'Save Complete',
                        text: 'Workspace was saved',
                        type: 'alert-success',
                    });
                    app.workspaceService.refresh();
                }
            });
        }
    }

    private deleteWorkspace(): void {
        const id = this.form.getItemValue('id');
        const name = this.form.getItemValue('name');

        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Confirm Delete',
            text: 'Delete Workspace: ' + name,
            callback: (result: boolean) => {
                if (result == true) {
                    VisionApplication.getInstance()
                        .workspaceService.deleteWorkspace(id)
                        .then((data) => {
                            if (data.ok) {
                                dhtmlx.message({
                                    title: 'Complete',
                                    text: 'Workspace was deleted',
                                    type: 'alert-success',
                                });
                                this.form.setItemValue('id', '');
                                this.form.setItemValue('name', '');
                                VisionApplication.getInstance().workspaceService.refresh();
                            }
                        });
                }
            },
        });
    }
}
